import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { Member } from 'src/app/features/member/member'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { CalendarSelectedDates } from 'src/app/shared/components/calendar/calendar-selected-dates'
import { CalendarService } from 'src/app/shared/components/calendar/calendar.service'
import { CalendarEvent } from 'src/app/shared/components/calendar/event'
import { CalenderViews } from 'src/app/shared/constants'
import { getEnumAsArray } from 'src/app/shared/utils'
import { environment } from 'src/environments/environment'

import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'

import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { RolesType } from '../../role/role'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { TimeBlock, TimeBlockType } from '../../time-block/time-block'
import { TimeBlockCreateEditModal } from '../../time-block/time-block-create-edit-modal/show.component'
import { timeBlockActions } from '../../time-block/time-block.actions'
import { timeBlockSelectors } from '../../time-block/time-block.selectors'
import { EmployeeBookingCreateModalComponent } from '../employee-booking/create-modal/show.component'
import {
  EmployeeBooking,
  EmployeeBookingActionTypes,
  EmployeeBookingTypes
} from '../employee-booking/employee-booking'
import { employeeBookingActions } from '../employee-booking/employee-booking.actions'
import { employeeBookingSelectors } from '../employee-booking/employee-booking.selectors'
import { EmployeeBookingService } from '../employee-booking/employee-booking.service'
import { EmployeeContractManagerLink } from '../employee-contract-manager-link/employee-contract-manager-link'
import { employeeContractManagerLinkSelectors } from '../employee-contract-manager-link/employee-contract-manager-link.selectors'
import { EmployeeContract } from '../employee-contract/employee-contract'
import { employeeContractSelectors } from '../employee-contract/employee-contract.selectors'
import { EmployeeContractService } from '../employee-contract/employee-contract.service'
import { SupervisorBookingDetailsModalComponent } from '../supervisor-booking/details-modal/show.component'
import { SupervisorBooking } from '../supervisor-booking/supervisor-booking'
import { supervisorBookingActions } from '../supervisor-booking/supervisor-booking.actions'
import { supervisorBookingSelectors } from '../supervisor-booking/supervisor-booking.selectors'
import { CalendarCheckedDateModalComponent } from './calendar-checked-date-modal/show.component'
import { Actions, ofType } from '@ngrx/effects'
import { translationSelectors } from '../../translation/translation.selectors'
import { SelectableItem } from 'src/app/shared/pipes/map-to-selectable-items.pipe'

export enum CalendarFilters {
  ALL = 'all',
  SUPERVISING = 'supervising',
  EMPLOYEE = 'employee',
  WEEK_CALENDAR = 'weekCalendar'
}

@UntilDestroy()
@Component({
  selector: 'my-work-schedule',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class MyWorkScheduleComponent implements OnInit {
  public isLoading$: Observable<boolean>
  public selectedCalendarDatesSubject: Subject<CalendarSelectedDates> =
    new BehaviorSubject(null)
  public selectedCalendarDates$ =
    this.selectedCalendarDatesSubject.asObservable()

  public member$: Observable<Member>
  public employeeBookings$: Observable<EmployeeBooking[]>
  public supervisorBookings$: Observable<SupervisorBooking[]>
  public bookingTypes = getEnumAsArray(EmployeeBookingTypes)
  public calendarEvents$: Observable<CalendarEvent[]>
  public employeeBookingActionTypes = EmployeeBookingActionTypes
  public contracts$: Observable<EmployeeContract[]>
  public form: UntypedFormGroup
  public selectedContracts$: Observable<EmployeeContract[]>
  public contractEmployeeBookings$: Observable<EmployeeBooking[]>
  public maxDate$: Observable<moment.Moment>
  public hasEmployeeRole$: Observable<boolean>
  public hasTrainerRole$: Observable<boolean>
  public contractIdsWithSupervisor: number[] = []
  public employeeTextConfig$: Observable<SafeHtml>
  public selectedLanguage$: Observable<string>
  public employeeModuleConfig: any
  public defaultCalendarView: CalenderViews = CalenderViews.MONTH
  public employeeContractManagerLinkEntities$: Observable<
    Dictionary<EmployeeContractManagerLink>
  >
  public allTimeBlocksOfMember$: Observable<TimeBlock[]>
  private allUnavailableTimeBlocks: TimeBlock[] = []
  private memberId: number
  private subscriptions: Subscription[] = []
  public selectableContracts: BehaviorSubject<SelectableItem[]>
  public agendaCloseDate: string = ''

  public bsModalRef: BsModalRef
  public lastSelectedDates: CalendarSelectedDates = {
    startDate: moment(),
    endDate: moment()
  }
  public calendarFilters = CalendarFilters
  public currentFilter: CalendarFilters
  public calendarViews = CalenderViews
  public currentFilter$: Subject<CalendarFilters> = new BehaviorSubject(null)
  public showDecline$: Subject<boolean> = new Subject()
  public showDecline: boolean = true
  public environment = environment

  constructor(
    private fb: UntypedFormBuilder,
    private modalService: BsModalService,
    private store: Store<AppState>,
    public employeeBookingService: EmployeeBookingService,
    private calendarService: CalendarService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    public actions$: Actions,
    private employeeContractService: EmployeeContractService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.employeeModuleConfig = environment.moduleConfigs.employeeModule

    this.employeeContractManagerLinkEntities$ = this.store.select(
      employeeContractManagerLinkSelectors.selectEntities
    )

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.form = this.fb.group({
      contractIds: [[]]
    })

    this.member$ = this.store.select(authSelectors.selectMember)

    this.hasEmployeeRole$ = this.store.select(
      userRoleSelectors.selectUserHasRoles,
      [RolesType.EMPLOYEE]
    )

    this.hasTrainerRole$ = this.store.select(
      userRoleSelectors.selectUserHasRoles,
      [RolesType.SUPERVISOR]
    )

    this.hasTrainerRole$
      .pipe(withLatestFrom(this.hasEmployeeRole$), untilDestroyed(this))
      .subscribe(([hasTrainerRole, hasEmployeeRole]) => {
        this.currentFilter = CalendarFilters.ALL
        this.currentFilter$.next(this.currentFilter)
      })

    this.member$.pipe(take(1)).subscribe((member) => {
      this.memberId = member?.id
      this.fetchTimeBlocksOfMember()

      this.allTimeBlocksOfMember$ = this.store.select(
        timeBlockSelectors.selectByFilter,
        {
          field: 'memberId',
          value: member?.id
        }
      )

      this.allTimeBlocksOfMember$
        .pipe(untilDestroyed(this))
        .subscribe(
          (allTimeBlocks) =>
            (this.allUnavailableTimeBlocks = allTimeBlocks.filter(
              (timeBlock) =>
                (timeBlock.isAvailable && timeBlock.price) ||
                timeBlock.isAvailable == 0 ||
                !timeBlock.isAvailable
            ))
        )
    })

    this.employeeTextConfig$ = this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(
        switchMap((language) =>
          this.store
            .select(
              configurationOptionSelectors.selectByKey,
              `${language}_employees_calendar_text`
            )
            .pipe(
              filter((configurationOption) => Boolean(configurationOption)),
              map((configurationOption) =>
                this.sanitizer.bypassSecurityTrustHtml(
                  configurationOption.value
                )
              )
            )
        )
      )

    this.selectedContracts$ = combineLatest([
      this.form.controls.contractIds.valueChanges,
      this.store.select(employeeContractSelectors.selectEntities),
      this.employeeContractManagerLinkEntities$
    ]).pipe(
      startWith([]),
      map(
        ([
          contractList,
          contractEntities,
          employeeContractManagerLinkEntities
        ]) => {
          const contractIds = contractList?.map((c: SelectableItem) => c.value)
          if (
            !contractIds ||
            !contractEntities ||
            !Object.values(contractEntities)?.length
          ) {
            return
          }

          if (contractIds.length == 1) {
            const contract = contractEntities[contractIds[0]]
            this.agendaCloseDate =
              this.employeeBookingService.getAgendaCloseDate(
                contract,
                employeeContractManagerLinkEntities
              ) ?? ''
          } else {
            this.agendaCloseDate = ''
          }

          this.contractIdsWithSupervisor = contractIds.filter(
            (contractId) =>
              contractEntities[contractId]?.employeeContractManagerLinks?.length
          )

          return contractIds.map((contractId) => contractEntities[contractId])
        }
      )
    )

    this.contracts$ = combineLatest([
      this.store.select(employeeContractManagerLinkSelectors.selectEntities),
      this.store.select(authSelectors.selectMember),
      this.store.select(employeeContractSelectors.selectAll)
    ]).pipe(
      map(
        ([employeeContractManagerLinkEntities, member, employeeContracts]) => {
          const filteredContracts =
            this.employeeContractService.getAvailableContracts(
              employeeContracts,
              employeeContractManagerLinkEntities,
              member
            )

          const currentContractIds = this.form.controls.contractIds.value || []

          if (!currentContractIds.length) {
            this.form.controls.contractIds.setValue(
              filteredContracts.map((contract) => {
                return { value: contract.id, label: contract.description }
              })
            )
          } else {
            this.form.controls.contractIds.setValue(currentContractIds)
          }

          return filteredContracts
        }
      )
    )

    this.isLoading$ = combineLatest([
      this.store.select(productSelectors.selectIsLoading),
      this.store.select(bookingSelectors.selectIsLoading)
    ]).pipe(map(([a, b]) => Boolean(a || b)))

    this.maxDate$ = combineLatest([
      this.store.select(
        configurationOptionSelectors.selectByKey,
        'employee_calendar_max_date_available'
      ),
      this.store.select(
        configurationOptionSelectors.selectByKey,
        'employee_calendar_days_ahead_available'
      )
    ]).pipe(
      filter(([fixedMaxDate, maxDaysAheadOption]) =>
        Boolean(fixedMaxDate || maxDaysAheadOption)
      ),
      map(([fixedMaxDateOption, maxDaysAheadOption]) => {
        const fixedMaxDate =
          fixedMaxDateOption && !!fixedMaxDateOption.value
            ? moment(fixedMaxDateOption.value)
            : moment().add(1, 'year')

        const maxDaysAhead =
          maxDaysAheadOption &&
          !!maxDaysAheadOption.value &&
          maxDaysAheadOption.value !== 'null'
            ? moment().add(maxDaysAheadOption.value, 'days')
            : moment().add(1, 'year')

        if (fixedMaxDate.unix() < maxDaysAhead.unix()) {
          return fixedMaxDate
        }

        return maxDaysAhead
      })
    )

    this.store
      .select(
        configurationOptionSelectors.selectByKey,
        `employee_calendar_days_ahead_available`
      )
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        map((configurationOption) => {
          return moment().add(configurationOption.value, 'days')
        })
      )

    this.employeeBookings$ = combineLatest([
      this.hasEmployeeRole$,
      this.store.select(employeeBookingSelectors.selectAll),
      this.selectedCalendarDates$,
      this.selectedContracts$,
      this.member$,
      this.translate.get('employeeModule.bookingType0'),
      this.translate.get('employeeModule.bookingType1'),
      this.translate.get('employeeModule.bookingType2'),
      this.translate.get('employeeModule.bookingType3'),
      this.translate.get('employeeModule.bookingType4')
    ]).pipe(
      map(
        ([
          hasEmployeeRole,
          employeeBookings,
          selectedCalendarDates,
          selectedContracts,
          member,
          bookingType0Translation,
          bookingType1Translation,
          bookingType2Translation,
          bookingType3Translation,
          bookingType4Translation
        ]) => {
          if (!employeeBookings?.length) {
            return []
          }

          const filteredEmployeeBookings = employeeBookings
            .filter((employeeBooking) => {
              if (
                moment(employeeBooking.startDate).valueOf() <
                  selectedCalendarDates?.startDate.valueOf() ||
                moment(employeeBooking.endDate).valueOf() >
                  selectedCalendarDates?.endDate.valueOf() ||
                employeeBooking.personId != member?.id
              ) {
                return false
              }

              if (employeeBooking.type == EmployeeBookingTypes.UNAVAILABLE) {
                return true
              }

              if (!selectedContracts?.length) {
                return true
              }

              const selectedContractIds = selectedContracts.map(
                (contract) => contract?.id
              )

              return selectedContractIds?.includes(employeeBooking.contractId)
            })
            .map((filteredBooking) => {
              let className = 'color' + `${filteredBooking?.colour}`

              if (filteredBooking.type == EmployeeBookingTypes.UNAVAILABLE) {
                className = 'bg-stripes unavailable-style'
              }

              let typeString = ''

              switch (filteredBooking.type) {
                case 0:
                  typeString = bookingType0Translation
                  break
                case 1:
                  typeString = bookingType1Translation
                  break
                case 2:
                  typeString = bookingType2Translation
                  break
                case 3:
                  typeString = bookingType3Translation
                  break
                case 4:
                  typeString = bookingType4Translation
                  break
              }

              return {
                ...filteredBooking,
                endDate: moment(filteredBooking.endDate)
                  .add(-1, 'minute')
                  .toISOString(),
                className,
                backgroundColor:
                  this.employeeBookingService.convertBookingColour(
                    filteredBooking?.colour
                  ),
                typeString
              }
            })

          return filteredEmployeeBookings
        }
      ),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
      startWith([])
    )

    this.supervisorBookings$ = this.store
      .select(supervisorBookingSelectors.selectAll)
      .pipe(
        startWith([]),
        withLatestFrom(this.member$),
        map(([supervisorBookings, member]) =>
          supervisorBookings.filter(
            (supervisorBooking) => supervisorBooking.personId == member?.id
          )
        )
      )

    this.calendarEvents$ = combineLatest([
      this.employeeBookings$,
      this.supervisorBookings$,
      this.currentFilter$,
      this.selectedContracts$,
      this.store.select(bookingSelectors.selectEntities),
      this.store.select(productSelectors.selectEntities),
      this.store.select(employeeContractSelectors.selectEntities),
      this.store.select(settingSelectors.selectById, 'language'),
      this.store.select(translationSelectors.selectAll),
      this.store.select(timeBlockSelectors.selectByFilter, {
        field: 'memberId',
        value: this.memberId
      })
    ]).pipe(
      map(
        ([
          employeeBookings,
          supervisorBookings,
          currentFilter,
          selectedContracts,
          bookingEntities,
          productEntities,
          employeeContractEntities,
          selectedLanguage,
          translations,
          timeBlocks
        ]) => {
          const selectedContractIds = selectedContracts?.map(
            (contract) => contract?.id
          )

          const employeeBookingCalEvents = employeeBookings
            .filter(
              (employeeBooking) =>
                selectedContractIds?.includes(employeeBooking.contractId) ||
                !employeeBooking.contractId
            )
            .map((employeeBooking) => {
              const description =
                translations.find(
                  (t) =>
                    t.productId == employeeBooking.productId &&
                    t.language == selectedLanguage
                )?.description ?? employeeBooking.description

              return this.calendarService.mapEmployeeBookingToCalEvent(
                employeeBooking,
                employeeContractEntities,
                description
              )
            })

          const supervisorBookingCalEvents = supervisorBookings.map(
            (supervisorBooking) => {
              const description =
                translations.find(
                  (t) =>
                    t.productId ==
                      bookingEntities[supervisorBooking.bookingId]
                        .linkedProductId && t.language == selectedLanguage
                )?.description ??
                productEntities[
                  bookingEntities[supervisorBooking.bookingId].linkedProductId
                ]?.description

              return this.calendarService.mapSupervisorBookingToCalEvent(
                supervisorBooking,
                bookingEntities[supervisorBooking.bookingId],
                selectedLanguage,
                description
              )
            }
          )

          const timeBlocksOfMember = timeBlocks
            .filter(
              (timeBlock) => timeBlock.price > 0 || !timeBlock.isAvailable
            )
            .map((timeBlock) =>
              this.calendarService.mapTimeBlockToCalEvent(
                {
                  ...timeBlock,
                  memberId: this.memberId
                },
                selectedLanguage
              )
            )

          switch (currentFilter) {
            case CalendarFilters.WEEK_CALENDAR:
              return [...timeBlocksOfMember]
            case CalendarFilters.SUPERVISING:
              return [...supervisorBookingCalEvents]
            case CalendarFilters.EMPLOYEE:
              return [...employeeBookingCalEvents]
            case CalendarFilters.ALL:
              return [
                ...employeeBookingCalEvents,
                ...supervisorBookingCalEvents
              ]
            default:
              return []
          }
        }
      )
    )
  }

  public onShowDecline(value?) {
    if (value != null && value != undefined) {
      this.showDecline = value
    } else {
      this.showDecline = !this.showDecline
    }

    this.showDecline$.next(this.showDecline)
  }
  public onChangeDates(selectedDates: CalendarSelectedDates): void {
    if (
      moment(selectedDates.startDate).diff(
        moment(this.lastSelectedDates?.startDate),
        'days'
      ) == 0 &&
      moment(selectedDates.endDate).diff(
        moment(this.lastSelectedDates?.endDate),
        'days'
      ) == 0
    ) {
      return
    }
    this.lastSelectedDates.startDate = moment(selectedDates.startDate)
    this.lastSelectedDates.endDate = moment(selectedDates.endDate)
    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        employeeBookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              personId: member.id,
              startDate: {
                [CondOperator.GREATER_THAN]:
                  selectedDates.startDate.toISOString()
              },
              endDate: {
                [CondOperator.LOWER_THAN]: `${selectedDates.endDate.format(
                  'YYYY-MM-DD'
                )}T23:59:59.999`
              }
            },
            join: [
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'employeeContracts' }
            ]
          })
        })
      )

      this.store.dispatch(
        supervisorBookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              personId: member.id,
              'booking.startDate': {
                [CondOperator.GREATER_THAN]:
                  selectedDates.startDate.toISOString()
              },
              'booking.endDate': {
                [CondOperator.LOWER_THAN]: selectedDates.endDate.toISOString()
              }
            },
            join: [
              { field: 'booking' },
              { field: 'booking.product' },
              { field: 'booking.linkedProduct' },
              { field: 'booking.product.translations' },
              { field: 'booking.linkedProduct.translations' }
            ]
          })
        })
      )
      this.actions$
        .pipe(ofType(employeeBookingActions.upsertMany), take(1))
        .subscribe((action) => {
          if (!action) {
            return
          }
          let productsIds = []
          for (const employeeB of action.entities) {
            if (
              employeeB.productId &&
              !productsIds.includes(employeeB.productId)
            ) {
              productsIds.push(employeeB.productId)
            }
          }
          if (productsIds.length > 0) {
            productsIds.forEach((productId) => {
              setTimeout(() => {
                this.store.dispatch(
                  timeBlockActions.fetchManyByItemTypeAndId({
                    itemId: productId,
                    timeBlockType: TimeBlockType.PRODUCT
                  })
                )
              }, 300)
            })
          }
        })
    })
  }

  public onClickDate(date: moment.Moment) {
    const dateObj = {
      startDate: moment(date).startOf('day'),
      endDate: moment(date).endOf('day')
    }

    // fetch the bookings again to see if a booking has been added via DMS
    this.onChangeDates(dateObj)

    if (this.currentFilter == CalendarFilters.WEEK_CALENDAR) {
      this.bsModalRef = this.modalService.show(TimeBlockCreateEditModal, {
        initialState: {
          memberId: this.memberId,
          timeBlock: {
            dayName: moment(date).format('dddd'),
            isoWeekDayNumber: moment(date).isoWeekday(),
            isAvailable: 0,
            price: 0,
            startTime: <any>moment(date).clone(),
            endTime: <any>moment(date).clone().add(1, 'hour'),
            blockLengthInMinutes: 60,
            days: [moment(date).isoWeekday()]
          },
          timeBlocks: this.allUnavailableTimeBlocks
        },
        class: '',
        ignoreBackdropClick: true
      })

      this.subscriptions.push(
        this.bsModalRef.content.updateTimeBlocks
          .pipe(take(1))
          .subscribe((updateTimeBlocks) => {
            if (updateTimeBlocks) {
              this.fetchTimeBlocksOfMember()
            }
          })
      )
    } else {
      this.hasTrainerRole$
        .pipe(take(1), withLatestFrom(this.hasEmployeeRole$))
        .subscribe(([hasTrainerRole, hasEmployeeRole]) => {
          this.allTimeBlocksOfMember$.pipe(take(1)).subscribe((timeblocks) => {
            this.modalService.show(EmployeeBookingCreateModalComponent, {
              initialState: {
                title: 'bookingModule.createBooking',
                employeeBooking: {
                  startDate: date,
                  endDate: date
                },
                allTimeBlocksOfMember: timeblocks,
                hasOnlyTrainerRole:
                  hasTrainerRole && !hasEmployeeRole ? true : false
              },
              class: 'modal-lg'
            })
          })
        })
    }
  }

  public onEventClicked(calendarEvent: CalendarEvent): void {
    if (this.currentFilter == this.calendarFilters.WEEK_CALENDAR) {
      const timeBlock = this.transformCalendarEventToTimeBlock(calendarEvent)

      if (timeBlock.price > 0 && timeBlock.isAvailable == 1) {
        // dont allow editing of irregularity timeblocks
        return
      }
      this.bsModalRef = this.modalService.show(TimeBlockCreateEditModal, {
        initialState: {
          memberId: this.memberId,
          timeBlock,
          timeBlocks: this.allUnavailableTimeBlocks
        },
        class: ''
      })

      this.subscriptions.push(
        this.bsModalRef.content.updateTimeBlocks
          .pipe(take(1))
          .subscribe((updateTimeBlocks) => {
            if (updateTimeBlocks) {
              this.fetchTimeBlocksOfMember()
            }
          })
      )
    } else {
      if (calendarEvent.params?.employeeBooking) {
        let bookingIsAlreadyChecked = false
        this.store
          .select(
            employeeContractSelectors.selectById,
            calendarEvent.params?.employeeBooking?.contractId
          )
          .pipe(
            take(1),
            filter((employeeContract) => Boolean(employeeContract)),
            withLatestFrom(
              this.store.select(
                employeeContractManagerLinkSelectors.selectEntities
              )
            ),
            tap(([employeeContract, employeeContractManagerLinkEntities]) => {
              let firstSupervisorCheck = null

              employeeContract?.employeeContractManagerLinks?.forEach(
                (employeeContractManagerLinkId) => {
                  if (
                    moment(
                      employeeContractManagerLinkEntities[
                        employeeContractManagerLinkId
                      ]?.checkedTillDate
                    ).isBefore(moment(firstSupervisorCheck)) ||
                    !firstSupervisorCheck
                  ) {
                    firstSupervisorCheck =
                      employeeContractManagerLinkEntities[
                        employeeContractManagerLinkId
                      ]?.checkedTillDate
                  }
                }
              )
              if (
                firstSupervisorCheck &&
                moment(firstSupervisorCheck).isAfter(
                  moment(calendarEvent.params?.employeeBooking.endDate)
                )
              ) {
                bookingIsAlreadyChecked = true
              }
            })
          )
          .subscribe()
        this.allTimeBlocksOfMember$.pipe(take(1)).subscribe((timeblocks) => {
          this.bsModalRef = this.modalService.show(
            EmployeeBookingCreateModalComponent,
            {
              initialState: {
                title: 'bookingModule.editBooking',
                employeeBooking: {
                  ...calendarEvent.params?.employeeBooking,
                  endDate: moment(
                    calendarEvent.params?.employeeBooking.endDate
                  ).add(1, 'minutes')
                },
                bookingIsAlreadyChecked,
                allTimeBlocksOfMember: timeblocks
              },
              class: 'modal-lg'
            }
          )
        })
      }
      if (calendarEvent.params?.supervisorBooking) {
        this.modalService.show(SupervisorBookingDetailsModalComponent, {
          initialState: {
            supervisorBookingId: calendarEvent.params?.supervisorBooking.id,
            bookingId: calendarEvent.params?.supervisorBooking.bookingId
          },
          class: 'modal-lg'
        })
      }
    }
  }

  public onClickCalendarChecked() {
    this.bsModalRef = this.modalService.show(
      CalendarCheckedDateModalComponent,
      {
        initialState: {
          contractIds: this.contractIdsWithSupervisor
        }
      }
    )

    this.bsModalRef.content.calendarCheckedDate
      .pipe(take(1), withLatestFrom(this.member$))
      .subscribe(([calendarCheckedDate, member]) => {
        this.store
          .select(
            employeeContractSelectors.selectById,
            calendarCheckedDate?.contractId
          )
          .pipe(take(1))
          .subscribe((contract) => {
            this.store.dispatch(
              employeeBookingActions.runAction({
                actionType: this.employeeBookingActionTypes.CLOSE_CALENDAR,
                personId: member.id,
                contractId: calendarCheckedDate?.contractId,
                params: {
                  calendarCheckedDate: calendarCheckedDate?.date
                }
              })
            )
            this.notificationService.emit('alerts.success', 'success')
          })
      })
  }

  public onChangeFilter(filter: CalendarFilters) {
    if (filter == this.currentFilter) {
      return
    }
    this.currentFilter = filter
    this.currentFilter$.next(filter)
  }

  public onClickSetUnavailability() {
    this.bsModalRef = this.modalService.show(TimeBlockCreateEditModal, {
      initialState: {
        memberId: this.memberId,
        timeBlock: {
          dayName: null,
          isoWeekDayNumber: null,
          isAvailable: false,
          price: null,
          startTime: null,
          endTime: null,
          blockLengthInMinutes: 0
        },
        timeBlocks: this.allUnavailableTimeBlocks
      },
      class: ''
    })

    this.subscriptions.push(
      this.bsModalRef.content.updateTimeBlocks
        .pipe(take(1))
        .subscribe((updateTimeBlocks) => {
          if (updateTimeBlocks) {
            this.fetchTimeBlocksOfMember()
          }
        })
    )
  }

  private fetchTimeBlocksOfMember() {
    // TODO: find a better solution to remove the modified time slots from the store
    this.store.dispatch(timeBlockActions.resetState())

    this.store.dispatch(
      timeBlockActions.fetchManyByItemTypeAndId({
        itemId: this.memberId,
        timeBlockType: TimeBlockType.MEMBER
      })
    )
  }

  private transformCalendarEventToTimeBlock(
    calendarEvent: CalendarEvent
  ): TimeBlock {
    if (!calendarEvent) {
      return null
    }

    const startHourAndMinute = moment(calendarEvent.start).format('HH:mm')
    const endHourAndMinute = moment(calendarEvent.end).format('HH:mm')

    return {
      ...calendarEvent.params.timeBlock,
      id: calendarEvent.id,
      dayName: moment(calendarEvent.start).format('dddd'),
      isoWeekDayNumber: moment(calendarEvent.start).isoWeekday(),
      startTime: moment(calendarEvent.start)
        .set('hour', Number(startHourAndMinute.slice(0, 2)))
        .set('minute', Number(startHourAndMinute.slice(-2)))
        .toISOString(),
      endTime: moment(calendarEvent.end)
        .set('hour', Number(endHourAndMinute.slice(0, 2)))
        .set('minute', Number(endHourAndMinute.slice(-2)))
        .toISOString(),
      blockLengthInMinutes: Number(
        moment
          .duration(moment(calendarEvent.end).diff(calendarEvent.start))
          .asMinutes()
          .toFixed(0)
      ),
      memberId: this.memberId,
      days: [moment(calendarEvent.start).isoWeekday()]
    }
  }

  adjustSelectedContracts(items: SelectableItem[]) {
    this.form.controls.contractIds.setValue(items)
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
